/** Отслеживание и отправка позиции чтения. */

import { api } from '../api'
import type { Manifest } from '../api'

/** Доля прочитанного. Считается так же, как на сервере, чтобы шкала совпадала. */
export function progressAt(manifest: Manifest, chapter: number, block: number): number {
  const total = manifest.total_chars
  if (!total || manifest.chapters.length === 0) return 0
  const index = Math.max(0, Math.min(chapter, manifest.chapters.length - 1))
  const info = manifest.chapters[index]
  let consumed = info.start_char
  if (info.blocks > 0 && info.chars > 0) {
    consumed += (Math.min(block, info.blocks) / info.blocks) * info.chars
  }
  return Math.max(0, Math.min(1, consumed / total))
}

/**
 * Копит изменения позиции и отправляет их пачкой.
 *
 * При прокрутке позиция меняется десятки раз в секунду; слать каждую — значит
 * заливать сервер запросами. Отправка идёт через паузу, а также немедленно при
 * уходе со страницы, чтобы последняя позиция не потерялась.
 */
export class PositionPusher {
  private timer: number | null = null
  private pending: { chapter: number; block: number; progress: number } | null = null
  private lastSent = ''

  constructor(
    private readonly bookId: number,
    private readonly delayMs = 2500,
  ) {}

  update(chapter: number, block: number, progress: number): void {
    this.pending = { chapter, block, progress }
    if (this.timer !== null) return
    this.timer = window.setTimeout(() => {
      this.timer = null
      void this.send(false)
    }, this.delayMs)
  }

  /** Отправить немедленно — при закрытии вкладки или уходе из читалки. */
  flush(): void {
    if (this.timer !== null) {
      window.clearTimeout(this.timer)
      this.timer = null
    }
    void this.send(true)
  }

  private async send(keepalive: boolean): Promise<void> {
    const pending = this.pending
    if (!pending) return
    const signature = `${pending.chapter}:${pending.block}`
    if (signature === this.lastSent) return
    this.lastSent = signature

    const payload = {
      book_id: this.bookId,
      chapter: pending.chapter,
      block: pending.block,
      char_offset: 0,
      progress: pending.progress,
      client_updated_at: Date.now() / 1000,
    }
    try {
      if (keepalive) {
        // keepalive позволяет запросу пережить закрытие вкладки.
        await fetch('/api/sync/push', {
          method: 'POST',
          keepalive: true,
          credentials: 'include',
          headers: authHeaders(),
          body: JSON.stringify({ positions: [payload] }),
        })
      } else {
        await api.pushPosition(payload)
      }
    } catch {
      // Позиция не критична: следующая прокрутка отправит её снова.
      this.lastSent = ''
    }
  }
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('chita.token')
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}
